import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
    Box,
    Button,
    Tabs,
    Tab,
    Tooltip,
} from "@mui/material";
import TabPanel from "../../../TabPanel";
import { api } from '../../../../redux/api';
import { useDispatch, useSelector } from 'react-redux';
import { isArray } from 'lodash';
import { t } from 'i18next';
import CommonGrid from '../../../Repository/CommonGrid';
import { ParentScreenCtx } from '../../../../pages/workbench_build/WorkbenchBuild';
import { treeActions } from '../../../../redux/slices/treeSlice';
import LightTooltip from '../../../../commonComponents/LightTooltip';
import { Loader } from '../../../../utils/loader';

const columnDefNameMaping = {
    name: t("DB_NAV_COLUMN_NAME"),
    dataType: t("DB_NAV_COLUMN_TYPE"),
    columnSize: t("DB_NAV_COLUMN_LENGTH"),
    isNullable: t("DB_NAV_COLUMN_NULLABLE"),
    typeName: t("DB_NAV_COLUMN_NATIVE_TYPE"),
    remarks: t("DB_NAV_COLUMN_REMARKS"),
};

const DatabaseViewer = ({ schema, connection, selectedTable, selectedMetaData, setSelectedMetaData, encoding, showAlert, handleQuery, setOpenDrawer, prepend }) => {
    const dispatch = useDispatch();
    const queryParams = useSelector((state) => state.queryParams);
    const parentScreen = useContext(ParentScreenCtx);
    const metaLoading = useSelector(state => state?.treeData?.[parentScreen]?.loadingState?.["META_LOADING"]?.isLoading || false)
    const top25Loading = useSelector(state => state?.treeData?.[parentScreen]?.loadingState?.["TOP25_LOADING"]?.isLoading || false)
    const metaError = useSelector(state => state?.treeData?.[parentScreen]?.loadingState?.["META_LOADING"]?.isError || false)
    const top25Error = useSelector(state => state?.treeData?.[parentScreen]?.loadingState?.["TOP25_LOADING"]?.isError || false)
    const [query, setQuery] = useState('');
    const [combineTables, setCombineTables] = useState('');
    const [currTab, setCurrTab] = useState(0);
    const [rowData, setRowData] = useState({
        metaData: [],
        top25: []
    });
    const [gridOptions, setGridOptions] = useState({
        metaData: {},
        top25: {}
    });
    const [selectedColumns, setSelectedColumns] = useState([]);
    const top25Timer = useRef();
    const metaTimer = useRef();

    const nothingToDisplay = useMemo(() => {
        return <div style={{
            width: '100%',
            height: '100%',
            fontSize: '16px',
            color: '#b5b2ba',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        }}>{t("TRACKING_REPORTS_NOTHING_TO_DISPLAY")}</div>
    })

    const onRowDataUpdated = (gridOptions) => {
        selectedMetaData?.forEach(e => {
            gridOptions.api.forEachNode(node => {
                if (node?.data?.name === e?.name) {
                    node.setSelected(true);
                }
            })
        })
    };

    const updateNewGridOptionsMeta = (data, type) => {
        const columnDefs = []
        Object.keys(columnDefNameMaping).forEach(e => {
            for (let i = 0; i < data.length; i++) {
                const ele = data[i];
                if (ele[e]) {
                    columnDefs.push({
                        headerName: columnDefNameMaping[e],
                        field: e,
                        resizable: true,
                        editable: false,
                        showPlaceholder: false
                    })
                    break;
                }
            }
        })
        setGridOptions(prev => { return { ...prev, metaData: { columnDefs } } });
    }

    const updateNewGridOptionsGeneric = (data = [], rowData = [], type) => {
        const columnDefs = [];
        for (let i = 0; i < data.length; i++) {
            for (let j = 0; j < rowData.length; j++) {
                if (rowData?.[j]?.[i]) {
                    columnDefs.push({
                        headerName: data[i],
                        field: data[i],
                        resizable: true,
                        editable: false,
                        showPlaceholder: false
                    })
                    break;
                }
            }
        }
        setGridOptions(prev => { return { ...prev, [type]: { columnDefs } } });
    }

    const handleTabChange = (e, newValue) => {
        setCurrTab(newValue);
    };

    const metaGridSelectionHandler = (gridOptions) => {
        const selectedRows = gridOptions.api.getSelectedRows();
        if (selectedRows.length > 0) {
            setSelectedMetaData(selectedRows);
        } else {
            setSelectedMetaData([]);
        }
    }

    const createQuery = () => {
        let columns = [];
        if (selectedMetaData?.length > 0) {
            let temp = '';
            selectedMetaData?.forEach((node, index) => { // Iterate over each node
                if (node?.name) {
                    if (prepend) {
                        temp += `${schema}.${selectedTable}.${node?.name}`;
                        columns.push(`${schema}.${selectedTable}.${node?.name}`);
                    }
                    else {
                        temp += `${selectedTable}.${node?.name}`;
                        columns.push(`${selectedTable}.${node?.name}`);
                    }
                    if (index < selectedMetaData.length - 1) { // Check if it's not the last node
                        temp += ','; // Append a comma
                    }
                }
            });
            setCombineTables((prev) => {
                return prev !== '' ? `${prev}, ${selectedTable}` : `${selectedTable}`
            })
            setQuery((prev) => {
                // Combine the current query with the previous one, appending a comma
                const combinedQuery = prev ? `${prev},${temp}` : temp; // If prev is falsy (empty or undefined), return temp directly
                // Append "FROM" to the combined query followed by the comma-separated selectedTable values
                return `${combinedQuery}`;
            });
            setSelectedColumns(columns);
            setOpenDrawer();
        }
    }

    useEffect(() => {
        if (query && combineTables) {
            let res = '';
            if (prepend) res = `SELECT ${query} FROM ${schema}.${combineTables}`;
            else res = `SELECT ${query} FROM ${combineTables}`;
            handleQuery({res: res, cols: selectedColumns });
        }
    }, [query, combineTables, prepend]);

    const getTableMetaData = () => {
        if (metaTimer.current) clearTimeout(metaTimer.current);
        dispatch(treeActions.setLoadingActions({ screen: parentScreen, key: "META_LOADING", data: { isLoading: true, isError: false } }));
        dispatch(
            api.endpoints.get_metadata.initiate(
                {
                    account: queryParams.account,
                    direction: parentScreen === "inbound" ? "IN" : "OUT",
                    table_name: selectedTable,
                    pdc_name: connection,
                    sort: false,
                    schema: schema,
                },
                { forceRefetch: true }
            )
        ).then((res) => {
            if (res?.data?.current_state === "SUCCESS" && isArray(res?.data?.data)) {
                let rowData = [...res?.data?.data || []];
                updateNewGridOptionsMeta(rowData);
                setRowData(prev => { return { ...prev, metaData: rowData } })
            }
            else {
                // const message = (!isArray(res?.data?.data) && res?.data?.data) ? res?.data?.data : res?.data?.message;
                // showAlert({ mssg: message, isOpen: true, type: 'warning' });
            }
        })
    }

    const updateTop25DataHandler = (sort) => {
        if (top25Timer.current) clearTimeout(top25Timer.current);
        dispatch(treeActions.setLoadingActions({ screen: parentScreen, key: "TOP25_LOADING", data: { isLoading: true, isError: false } }));
        dispatch(
            api.endpoints.get_top_25_records.initiate(
                {
                    account: queryParams.account,
                    direction: parentScreen === "inbound" ? "IN" : "OUT",
                    table_name: selectedTable,
                    pdc_name: connection,
                    encoding: encoding,
                    schema: schema,
                },
                { forceRefetch: true }
            )
        ).then((res) => {
            if (res?.data?.current_state === "SUCCESS" && isArray(res?.data?.data)) {
                const rowData = [...res?.data?.data || []];
                const headers = rowData.splice(0, 1)[0];
                if (rowData[0] && !rowData?.[0]?.[0]?.includes(t('LBL_NO_RECORDS'))) {
                    updateNewGridOptionsGeneric(headers, rowData, "top25");
                    const newRowData = rowData.map(e => {
                        const ele = {}
                        for (let i = 0; i < e.length; i++) {
                            ele[headers[i]] = e[i];
                        }
                        return ele;
                    })
                    setRowData(prev => { return { ...prev, top25: newRowData } })
                }
                // setPreviousSelectedTable(prev => { return { ...prev, top25: schema + "." + selectedTable } })
            }
            else {
                const message = (!isArray(res?.data?.data) && res?.data?.data) ? res?.data?.data : res?.data?.message;
                showAlert({ mssg: message, isOpen: true, type: 'warning' });
            }
        })
    };

    useEffect(() => {
        const mssg = t('BOX_MSG_GENERAL_ERROR')
        if (metaError && metaLoading) {
            showAlert({ mssg, isOpen: true, type: 'warning' });
            dispatch(treeActions.setLoadingActions({ screen: parentScreen, key: "META_LOADING", data: { isLoading: false, isError: false } }));
        }
        if (top25Error && top25Loading) {
            showAlert({ mssg, isOpen: true, type: 'warning' });
            dispatch(treeActions.setLoadingActions({ screen: parentScreen, key: "TOP25_LOADING", data: { isLoading: false, isError: false } }));
        }
    }, [metaError, top25Error])

    useEffect(() => {
        // setPreviousSelectedTable({metaData: '', top25: ''})
        setSelectedMetaData([]);
        // setGridOptions(prev => { return {...prev, metaData: {}, top25: {}, sqlResult: {}}})
        // setRowData(prev => { return {metaData: [], top25: [], sqlResult: [], compareSQL: []} })
    }, [selectedTable]);

    useEffect(() => {
        if (currTab === 0) {
            setGridOptions(prev => { return { ...prev, metaData: {} } });
            getTableMetaData();
        } else if (currTab === 1) {
            setGridOptions(prev => { return { ...prev, top25: {} } });
            updateTop25DataHandler();
        }
    }, [currTab, selectedTable])

    return (
        <div className="height-100">
            <Box sx={{ borderBottom: 1, borderColor: "divider", display: 'flex' }}>
                <Tabs onChange={handleTabChange} value={currTab}>
                    <Tab
                        label={
                            <div className="flex align-center">
                                <span className="flex align-center">
                                    <span className="BALLOT screen-tab-icons"></span>
                                    <span></span>
                                    {t("DB_NAV_TAB_METADATA")}
                                </span>
                            </div>
                        }
                    />
                    <Tab
                        label={
                            <div className="flex align-center">
                                <span className="flex align-center">
                                    <span className="TABLE screen-tab-icons"></span>
                                    <span>{t("DB_NAV_TAB_TOP25_RECS")}</span>
                                </span>
                            </div>
                        }
                    />
                </Tabs>
                {currTab === 0 ?  (
                    <LightTooltip title={t('DB_NAV_ADD_SELECTED_FIELDS')}>
                        <span>
                            <Button size="small" onClick={createQuery} disabled={!selectedMetaData?.length} variant="outlined">
                                {t('ADD')}
                            </Button>
                        </span>
                    </LightTooltip>
                ) : (
                    ''
                )}
            </Box>
            {currTab === 0 && (
                <TabPanel
                    value={currTab}
                    className={"height-100 overflow-auto"}
                    index={0}
                >
                    {
                        metaLoading ? <Loader/> :
                        <Box sx={{ position: 'relative' }} className={"height-100 overflow-auto"}>
                            {
                                // metaLoading ? <Loader /> : 
                                Object.keys(gridOptions.metaData).length ? <CommonGrid
                                    gridOptions={{
                                        ...gridOptions.metaData,
                                        onSelectionChanged: metaGridSelectionHandler
                                    }}
                                    rowData={rowData.metaData}
                                    containerClasses="database-navigator-right-grid"
                                    gridProps={{
                                        rowSelection: "multiple",
                                        onRowDataUpdated: onRowDataUpdated
                                    }}
                                /> : nothingToDisplay}
                        </Box>
                    }
                </TabPanel>
            )}
            {currTab === 1 && (
                <TabPanel
                    value={currTab}
                    index={1}
                    className={"height-100 overflow-auto"}
                >
                    {top25Loading ? <Loader/> : Object.keys(gridOptions.top25).length ?  <CommonGrid
                        fullSize={false}
                        gridOptions={{...gridOptions.top25}}
                        rowData={rowData.top25}
                        containerClasses="database-navigator-right-grid"
                    /> : nothingToDisplay}
                </TabPanel>
            )}
        </div>
    )
}

export default DatabaseViewer